import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { StarRating } from "@/components/StarRating";

// Simple auto-rotating testimonials carousel.
// Props: reviews = [{ author_name, comment, rating, vehicle_name }]
export default function ReviewsCarousel({ reviews = [], intervalMs = 5500 }) {
  const [index, setIndex] = useState(0);
  const timer = useRef(null);

  const items = useMemo(() => reviews.filter(Boolean), [reviews]);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, intervalMs);
    return () => clearInterval(timer.current);
  }, [count, intervalMs]);

  const restartTimer = () => {
    if (timer.current) clearInterval(timer.current);
    if (count > 1) {
      timer.current = setInterval(() => setIndex((i) => (i + 1) % count), intervalMs);
    }
  };

  if (count === 0) return null;

  const goTo = (i) => { setIndex(((i % count) + count) % count); restartTimer(); };
  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  const current = items[index];

  return (
    <div className="relative" data-testid="reviews-carousel">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 md:p-12 min-h-[240px] flex flex-col justify-between transition-opacity">
        <Quote size={36} className="text-[#E2AB20]/30" />
        <blockquote className="mt-3 text-lg md:text-2xl font-heading text-[#010F27] leading-relaxed" data-testid="carousel-quote">
          "{current.comment}"
        </blockquote>
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-[#010F27]" data-testid="carousel-author">{current.author_name}</div>
            {current.vehicle_name && (
              <div className="text-xs text-[#4B5B76]">Rented the {current.vehicle_name}</div>
            )}
          </div>
          <StarRating value={current.rating || 5} size={16} showValue={false} />
        </div>
      </div>

      {/* Controls */}
      {count > 1 && (
        <div className="mt-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Show review ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === index ? "bg-[#E2AB20] w-8" : "bg-gray-300 w-2 hover:bg-gray-400"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={prev}
              className="h-10 w-10 rounded-full border border-gray-200 grid place-items-center text-[#4B5B76] hover:border-[#E2AB20] hover:text-[#E2AB20] transition-colors"
              aria-label="Previous review"
              data-testid="carousel-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              className="h-10 w-10 rounded-full border border-gray-200 grid place-items-center text-[#4B5B76] hover:border-[#E2AB20] hover:text-[#E2AB20] transition-colors"
              aria-label="Next review"
              data-testid="carousel-next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
